import React, { useState } from 'react';
import SMSelectedReviewSectionActionBar, { SMSelectedReviewSectionTab } from './action-bar';
import styles from './index.module.scss';
import SMSelectedReviewSectionActivityTab from './tabs/activity';
import SMSelectedReviewSectionNotesTab from './tabs/notes';
import SMSelectedReviewSectionSubjectTab from './tabs/subject';
import SMSelectedReviewSummaryTab from './tabs/summary';
import Card from '@components-clean/core/card';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import { useTranslation } from '@hooks/index';
import { SubjectMonitoringStudyMemberApi } from '@apis/bff-api/subject-monitoring/subject-monitoring-study-member-api';
import { SubjectReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-review-instance';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type Props = {
    studyRef: string;
    reviewRecordId: number;
};

const SMSelectedReviewSection = ({ studyRef, reviewRecordId }: Props) => {
    const { t: tCommon } = useTranslation('common');

    const getReviewQuery = SubjectMonitoringReviewAPI.useGetByRecordIdQuery(studyRef, reviewRecordId);
    const getReviewSubjectsQuery = SubjectReviewInstanceApi.useGetSubjects(studyRef, reviewRecordId);
    const getSmStudyMembersQuery = SubjectMonitoringStudyMemberApi.useGetAllStudyMembers(studyRef, true);

    const review = getReviewQuery.data;
    const reviewSubjects = getReviewSubjectsQuery.data;
    const smStudyMembers = getSmStudyMembersQuery.data;

    const isLoading = getReviewQuery.isLoading || getReviewSubjectsQuery.isLoading || getSmStudyMembersQuery.isLoading;
    const isError = !!getReviewQuery.error || !!getReviewSubjectsQuery.error || !!getSmStudyMembersQuery.error;

    const [selectedTab, setSelectedTab] = useState<SMSelectedReviewSectionTab>('summary');

    function getTabContent() {
        switch (selectedTab) {
            case 'summary':
                return (
                    review != null &&
                    smStudyMembers != null &&
                    reviewSubjects != null && (
                        <SMSelectedReviewSummaryTab
                            review={review}
                            smStudyMembers={smStudyMembers}
                            reviewSubjects={reviewSubjects}
                            studyRef={studyRef}
                        />
                    )
                );
            case 'subject':
                return (
                    review != null &&
                    reviewSubjects != null && (
                        <SMSelectedReviewSectionSubjectTab
                            studyRef={studyRef}
                            review={review}
                            reviewSubjects={reviewSubjects}
                        />
                    )
                );
            case 'activity':
                return (
                    review?.id != null && (
                        <SMSelectedReviewSectionActivityTab studyRef={studyRef} reviewId={review.id} />
                    )
                );
            case 'notes':
                return (
                    review?.id != null && <SMSelectedReviewSectionNotesTab studyRef={studyRef} reviewId={review.id} />
                );

            default:
                return null;
        }
    }

    return (
        <Card
            data-testid="active-review-tab-content"
            title={
                <SMSelectedReviewSectionActionBar
                    reviewRecordId={reviewRecordId}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                />
            }
            className={styles.tabContent}
            bodyClassName={styles.tabContentInner}
        >
            {isLoading || isError ? (
                <div className={styles.cardLoadingStateWrapper}>
                    {isError ? (
                        <InfoField type="Error" message={tCommon('errorMessage')} />
                    ) : isLoading ? (
                        <Loader />
                    ) : null}
                </div>
            ) : (
                getTabContent()
            )}
        </Card>
    );
};

export default SMSelectedReviewSection;
